// Location: src/components/admin/analytics/components/SalesCard.tsx
// Purpose: Reusable component for displaying sales/conversions with pagination
// Scope: Used in affiliate analytics views

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Target, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

interface Sale {
	_id: string;
	conversionType: string;
	conversionEmail?: string;
	conversionDate: string;
	utmSource?: string;
	utmCampaign?: string;
	landingPage: string;
	latestAttributionUpdate?: string;
	latestAttributionGameId?: string;
}

interface SalesCardProps {
	affiliateAnalytics: any | undefined;
	formatNumber: (num: number | undefined) => string;
}

export function SalesCard({ affiliateAnalytics, formatNumber }: SalesCardProps) {
	const [currentPage, setCurrentPage] = useState(1);
	const [isAnimating, setIsAnimating] = useState(false);
	const itemsPerPage = 10;

	// Filter sales from recent conversions (exclude email_signup)
	const sales = useMemo(() => {
		if (!affiliateAnalytics?.recentConversions) return [];
		return affiliateAnalytics.recentConversions.filter(
			(conversion: Sale) => conversion.conversionType !== 'email_signup'
		);
	}, [affiliateAnalytics]);

	const totalPages = Math.ceil(sales.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const currentPageData = sales.slice(startIndex, endIndex);

	const handlePageChange = async (newPage: number) => {
		if (newPage === currentPage || isAnimating) return;

		setIsAnimating(true);
		await new Promise((resolve) => setTimeout(resolve, 150)); // Short delay for animation
		setCurrentPage(newPage);
		setTimeout(() => setIsAnimating(false), 150);
	};

	return (
		<Card className='bg-gradient-to-br from-pink-500/5 to-purple-500/10 border-pink-500/20'>
			<CardHeader>
				<CardTitle className='flex items-center justify-between'>
					<div className='flex items-center gap-2'>
						<Target className='h-5 w-5 text-pink-500' />
						SALES
					</div>
					<Badge
						variant='secondary'
						className='bg-pink-500/10 text-pink-600 border-pink-500/20'
					>
						{formatNumber(sales.length)} total
					</Badge>
				</CardTitle>
			</CardHeader>
			<CardContent>
				{sales.length > 0 ? (
					<div className='space-y-4'>
						{/* SALES list with animation */}
						<div
							className={cn(
								'space-y-3 transition-all duration-300 ease-in-out',
								isAnimating
									? 'opacity-0 transform translate-y-2'
									: 'opacity-100 transform translate-y-0'
							)}
						>
							{currentPageData.map(
								(sale: Sale, index: number) => (
									<div
										key={`${sale._id}-${currentPage}`}
										className={cn(
											'flex items-center justify-between p-4 bg-white/50 dark:bg-gray-900/50 rounded-lg border border-pink-200/50 dark:border-pink-800/30 transition-all duration-300 ease-out hover:bg-pink-50/50 dark:hover:bg-pink-900/20 hover:border-pink-300/50 hover:shadow-md',
											'animate-in fade-in slide-in-from-bottom-2'
										)}
										style={{
											animationDelay: `${index * 50}ms`,
											animationFillMode: 'both',
										}}
									>
										<div className='flex items-center gap-3'>
											<div className='h-10 w-10 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0'>
												<Target className='h-4 w-4 text-white' />
											</div>
											<div className='min-w-0'>
												<div className='font-medium text-pink-700 dark:text-pink-300 truncate'>
													{sale.conversionEmail ||
														'New Watcher'}
												</div>
												<div className='text-sm text-muted-foreground'>
													{format(
														new Date(
															sale.conversionDate
														),
														'MMM d, yyyy h:mm a'
													)}
												</div>
												{(sale.utmSource ||
													sale.utmCampaign ||
													sale.latestAttributionUpdate) && (
													<div className='flex items-center gap-2 mt-1'>
														{sale.utmSource && (
															<Badge
																variant='outline'
																className='text-xs px-2 py-0 bg-blue-50 text-blue-600 border-blue-200'
															>
																{sale.utmSource}
															</Badge>
														)}
														{sale.utmCampaign && (
															<Badge
																variant='outline'
																className='text-xs px-2 py-0 bg-purple-50 text-purple-600 border-purple-200'
															>
																{
																	sale.utmCampaign
																}
															</Badge>
														)}
														{sale.latestAttributionUpdate &&
															sale.latestAttributionGameId && (
																<Badge
																	variant='outline'
																	className='text-xs px-2 py-0 bg-amber-50 text-amber-600 border-amber-200'
																	title={`Attribution updated ${format(
																		new Date(
																			sale.latestAttributionUpdate
																		),
																		'MMM d, h:mm a'
																	)}`}
																>
																	Updated by{' '}
																	{
																		sale.latestAttributionGameId
																	}
																</Badge>
															)}
													</div>
												)}
											</div>
										</div>
										<div className='text-right flex-shrink-0'>
											<div className='font-bold text-pink-600 text-sm'>
												100 KARMA
											</div>
											<div className='text-xs text-muted-foreground'>
												Watcher Reward
											</div>
										</div>
									</div>
								)
							)}
						</div>

						{/* Beautiful Pagination */}
						{totalPages > 1 && (
							<div className='flex items-center justify-between pt-4 border-t border-pink-200/30'>
								<div className='text-sm text-muted-foreground'>
									Showing {startIndex + 1}-
									{Math.min(endIndex, sales.length)} of{' '}
									{sales.length} sales
								</div>

								<div className='flex items-center gap-2'>
									<Button
										variant='outline'
										size='sm'
										onClick={() =>
											handlePageChange(currentPage - 1)
										}
										disabled={
											currentPage === 1 || isAnimating
										}
										className={cn(
											'h-8 w-8 p-0 border-pink-200 hover:bg-pink-50 hover:border-pink-300 disabled:opacity-50 transition-all duration-200',
											currentPage === 1
												? 'cursor-not-allowed'
												: 'hover:shadow-sm'
										)}
									>
										<ChevronLeft className='h-4 w-4' />
									</Button>

									{/* Page numbers */}
									<div className='flex items-center gap-1'>
										{Array.from(
											{ length: totalPages },
											(_, i) => i + 1
										).map((pageNum) => {
											const isActive =
												pageNum === currentPage;
											const isNear =
												Math.abs(
													pageNum - currentPage
												) <= 1;
											const isFirst = pageNum === 1;
											const isLast =
												pageNum === totalPages;

											if (
												!isNear &&
												!isFirst &&
												!isLast &&
												totalPages > 5
											) {
												if (
													pageNum ===
														currentPage - 2 ||
													pageNum === currentPage + 2
												) {
													return (
														<span
															key={pageNum}
															className='text-muted-foreground text-sm'
														>
															...
														</span>
													);
												}
												return null;
											}

											return (
												<Button
													key={pageNum}
													variant={
														isActive
															? 'default'
															: 'outline'
													}
													size='sm'
													onClick={() =>
														handlePageChange(
															pageNum
														)
													}
													disabled={isAnimating}
													className={cn(
														'h-8 w-8 p-0 transition-all duration-200',
														isActive
															? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white border-transparent shadow-md hover:shadow-lg'
															: 'border-pink-200 hover:bg-pink-50 hover:border-pink-300 hover:shadow-sm'
													)}
												>
													{pageNum}
												</Button>
											);
										})}
									</div>

									<Button
										variant='outline'
										size='sm'
										onClick={() =>
											handlePageChange(currentPage + 1)
										}
										disabled={
											currentPage === totalPages ||
											isAnimating
										}
										className={cn(
											'h-8 w-8 p-0 border-pink-200 hover:bg-pink-50 hover:border-pink-300 disabled:opacity-50 transition-all duration-200',
											currentPage === totalPages
												? 'cursor-not-allowed'
												: 'hover:shadow-sm'
										)}
									>
										<ChevronRight className='h-4 w-4' />
									</Button>
								</div>
							</div>
						)}
					</div>
				) : (
					<div className='text-center text-muted-foreground py-12'>
						<Target className='h-12 w-12 mx-auto mb-4 text-pink-400/50' />
						<div className='text-lg font-medium mb-2'>
							No SALES Yet
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}